import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, Send, ArrowRight, Loader2 } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { useDataLabel } from '@/lib/dataI18n';

/**
 * Renders the "Suite questionnaires" block — every published questionnaire
 * attached to `suiteCode`, with a quick summary (sections × questions) and
 * View / Assign action buttons. Reusable across the bespoke suite consoles
 * (TalentMap, Climara, TalentCompetency, TalentTrain) and the generic
 * SuiteConsole.
 */
export default function SuiteQuestionnaireBlock({ suiteCode, suiteName }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tData = useDataLabel();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const qs = await adminApi.listQuestionnaires();
        if (!alive) return;
        const filtered = (qs || []).filter((q) =>
          (q.suite_codes || []).includes(suiteCode) &&
          // Library MVP — only published evaluations are consumable by suites.
          q.status === 'published',
        );
        // Hydrate each with section + question counts from its current version
        const hydrated = await Promise.all(
          filtered.map(async (q) => {
            if (!q.current_version_id) return { ...q, _sections: 0, _questions: 0, _version: null };
            try {
              const detail = await adminApi.getQuestionnaire(q.id);
              const v = (detail.versions || []).find((vv) => vv.id === q.current_version_id);
              return {
                ...q,
                _sections: v?.sections?.length || 0,
                _questions: v?.questions?.length || 0,
                _version: v?.version_number || null,
              };
            } catch {
              return { ...q, _sections: 0, _questions: 0, _version: null };
            }
          }),
        );
        if (alive) setItems(hydrated);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [suiteCode]);

  return (
    <Card
      className="p-0 shadow-card overflow-hidden"
      data-testid={`suite-qnr-block-${suiteCode}`}
    >
      <div className="px-6 py-5 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="font-display font-bold text-lg tracking-tight">
            {t('pages.suiteConsole.suiteQnrs')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('pages.suiteConsole.qnrsSeeded', { name: suiteName })}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/admin/questionnaires')}
          className="gap-1.5"
        >
          {t('pages.suiteConsole.manage')} <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
      {loading ? (
        <div className="p-8 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('pages.suiteConsole.loading')}
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">
          {t('pages.suiteConsole.noQnrs', { name: suiteName })}
        </div>
      ) : (
        <div className="divide-y divide-border">
          {items.map((q) => (
            <div
              key={q.id}
              data-testid={`suite-${suiteCode}-qnr-${q.code}`}
              className="px-6 py-4 flex items-start justify-between gap-4 flex-wrap"
            >
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => navigate(`/admin/questionnaires/${q.id}`)}
                  data-testid={`suite-${suiteCode}-qnr-name-${q.code}`}
                  className="text-left font-medium hover:text-primary hover:underline transition-colors"
                >
                  {tData(q.name)}
                </button>
                <p className="text-xs text-muted-foreground mt-0.5">{tData(q.description)}</p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <code className="text-xs text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded">
                    {q.code}
                  </code>
                  {q._version && (
                    <Badge variant="outline" className="text-xs">v{q._version}</Badge>
                  )}
                  <Badge variant="outline" className="text-xs">
                    {t('pages.questionnaireDetail.sectionsCount')}: {q._sections}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {t('pages.questionnaireDetail.questionsCount')}: {q._questions}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge className={
                  q.status === 'active'
                    ? 'bg-success/10 text-success border-success/20'
                    : 'bg-muted text-muted-foreground border-border'
                }>
                  {t(`common.${q.status}`, { defaultValue: q.status })}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`suite-${suiteCode}-qnr-view-${q.code}`}
                  onClick={() => navigate(`/admin/questionnaires/${q.id}`)}
                  className="gap-1.5"
                >
                  <Eye className="h-3.5 w-3.5" /> {t('pages.suiteConsole.viewQnr')}
                </Button>
                <Button
                  size="sm"
                  data-testid={`suite-${suiteCode}-qnr-assign-${q.code}`}
                  onClick={() => navigate(`/admin/questionnaires/${q.id}?assign=1`)}
                  className="bg-gradient-primary gap-1.5"
                >
                  <Send className="h-3.5 w-3.5" /> {t('pages.suiteConsole.assignQnr')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
